"use client";

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  ReactNode,
} from "react";

// Map of indexId -> array of thought IDs visible in that index
type IndexHitsMap = Record<string, string[]>;

export type StudioIndexHitEntry = {
  indexId: string;
  thoughtIds: string[];
};

interface StudioIndexHitsContextValue {
  /**
   * Register/update the visible thought IDs for a given index
   * @param indexId - The unique identifier for the MeiliSearch Index (e.g., "check-job_activity-situation123")
   * @param thoughtIds - Array of thought IDs currently visible in that index
   */
  registerIndexHits: (indexId: string, thoughtIds: string[]) => void;

  /**
   * Remove an index from the visible hit registry when its search surface
   * unmounts.
   */
  unregisterIndexHits: (indexId: string) => void;

  /**
   * Get the visible thought IDs for a specific index
   * @param indexId - The unique identifier for the MeiliSearch Index
   * @returns Array of thought IDs, empty array if index not found
   */
  getIndexHits: (indexId: string) => string[];

  /**
   * Get all registered visible hit groups for screen-level actions.
   */
  getIndexHitEntries: () => StudioIndexHitEntry[];
}

const StudioIndexHitsContext =
  createContext<StudioIndexHitsContextValue | null>(null);

export function StudioIndexHitsProvider({ children }: { children: ReactNode }) {
  const [indexHitsMap, setIndexHitsMap] = useState<IndexHitsMap>({});

  const registerIndexHits = useCallback(
    (indexId: string, thoughtIds: string[]) => {
      setIndexHitsMap((currentMap) => {
        const prev = currentMap[indexId];

        if (
          prev &&
          prev.length === thoughtIds.length &&
          prev.every((id, idx) => id === thoughtIds[idx])
        ) {
          return currentMap;
        }

        return {
          ...currentMap,
          [indexId]: thoughtIds,
        };
      });
    },
    [],
  );

  const unregisterIndexHits = useCallback((indexId: string) => {
    setIndexHitsMap((currentMap) => {
      if (!currentMap[indexId]) {
        return currentMap;
      }

      const nextIndexHitsMap = { ...currentMap };
      delete nextIndexHitsMap[indexId];
      return nextIndexHitsMap;
    });
  }, []);

  const getIndexHits = useCallback((indexId: string) => {
    return indexHitsMap[indexId] || [];
  }, [indexHitsMap]);

  const getIndexHitEntries = useCallback(() => {
    return Object.entries(indexHitsMap).map(
      ([indexId, thoughtIds]) => ({
        indexId,
        thoughtIds,
      }),
    );
  }, [indexHitsMap]);

  const contextValue = useMemo(
    () => ({
      registerIndexHits,
      unregisterIndexHits,
      getIndexHits,
      getIndexHitEntries,
    }),
    [
      getIndexHitEntries,
      getIndexHits,
      registerIndexHits,
      unregisterIndexHits,
    ],
  );

  return (
    <StudioIndexHitsContext.Provider value={contextValue}>
      {children}
    </StudioIndexHitsContext.Provider>
  );
}

/**
 * Hook to access Studio index hits context
 * Returns no-op functions if used outside provider (graceful degradation)
 */
export function useStudioIndexHits() {
  const context = useContext(StudioIndexHitsContext);
  if (!context) {
    // Return no-op functions for graceful degradation
    return {
      registerIndexHits: () => {},
      unregisterIndexHits: () => {},
      getIndexHits: () => [],
      getIndexHitEntries: () => [],
    };
  }
  return context;
}
